import {
  ButtonSize,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Close } from "@mui/icons-material"
import { Box, Checkbox, Chip, IconButton, TextField } from "@mui/material"
import { Smiley, SmileyMeh, SmileySad, SmileyWink } from "@phosphor-icons/react"
import React, { useState } from "react"
import { useAppContext } from "../../app/Context"
import { useMixpanel } from "../../app/hooks/useMixpanel"
import { getAccentColor } from "../../constants/colors"
import { AnalyticsEvents } from "../../types/Analytics"
import { FeedbackEntity, Feedback as TFeedback } from "../../types/Feedback"
import { createFeedback } from "../../utils/general"

const feedbackTopics = [
  "Audio quality",
  "Avatar quality",
  "Responses",
  "Interface",
  "Chat",
] as const
type FeedbackTopic = (typeof feedbackTopics)[number]

interface FeedbackProps {
  visible: boolean
  onClose: () => void
  onOpen: () => void
  onSubmit: () => void
}

const selectedBg = "var(--primitives-desktop-primary-white-100)"
const unselectedBg = "var(--primitives-desktop-primary-white-40)"

export default function Feedback({
  visible,
  onClose,
  onOpen,
  onSubmit,
}: FeedbackProps) {
  const [rating, setRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<string>("")
  const [contactConsent, setContactConsent] = useState<boolean>(false)
  const [feedbackTopic, setFeedbackTopic] = useState<FeedbackTopic>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const context = useAppContext()
  const { logAnalyticsEvent } = useMixpanel()

  const handleSubmitFeedback = async () => {
    try {
      setIsSubmitting(true)
      const feedbackPayload: Partial<TFeedback> = {
        feedback,
        rating,
        feedbackTopic,
        contactConsent,
        entityType: FeedbackEntity.CALL,
        entityId: context.call.id,
        creatorId: context.user.uid,
      }
      await createFeedback(feedbackPayload)
      logAnalyticsEvent(AnalyticsEvents.Feedback, feedbackPayload)
    } catch (error) {
      console.warn("Error submitting feedback", error)
      setError("Something went wrong. Please try again.")
    }
    setIsSubmitting(false)
  }

  const handleClearFeedback = () => {
    setFeedback("")
    setRating(null)
    setContactConsent(false)
    setFeedbackTopic(feedbackTopics[0])
  }

  const handleSubmit = async () => {
    if (!rating) {
      setError("Please rate the product")
      return
    }
    setError(null)
    await handleSubmitFeedback()
    handleClearFeedback()
    onClose()
    onSubmit()
  }

  const handlePressChip = (feedbackTopicArg: FeedbackTopic) => {
    setFeedbackTopic(
      feedbackTopicArg === feedbackTopic ? null : feedbackTopicArg,
    )
  }

  const handlePressOption = (ratingArg: number) => {
    if (rating === ratingArg) {
      setRating(null)
      return
    }

    setRating(ratingArg)
    if (!visible) {
      onOpen()
    }
  }

  return (
    <Box sx={styles.modalOverlay}>
      <Box sx={styles.modalContent}>
        {visible && (
          <IconButton onClick={onClose} sx={styles.closeButton}>
            <Close />
          </IconButton>
        )}
        <Typography
          size={TypographySize.HeadingXS}
          weight={TypographyWeight.Bold}
          color="var(--colors-light-text-text-white)"
        >
          {"How happy are you with your call experience?"}
        </Typography>
        {error && (
          <Typography
            size={TypographySize.TextS}
            weight={TypographyWeight.Regular}
            color={"var(--primitives-desktop-primary-red-700)"}
          >
            {error}
          </Typography>
        )}
        <Box sx={styles.feedbackOptions}>
          <FeedbackOption
            Icon={SmileySad}
            text="Not what I expected"
            onClick={() => handlePressOption(1)}
            selected={rating === 1}
          />
          <FeedbackOption
            Icon={SmileyMeh}
            text="Fine, but not perfect"
            onClick={() => handlePressOption(2)}
            selected={rating === 2}
          />
          <FeedbackOption
            Icon={Smiley}
            text="Meets my expectation"
            onClick={() => handlePressOption(3)}
            selected={rating === 3}
          />
          <FeedbackOption
            Icon={SmileyWink}
            text="Happy with the result!"
            onClick={() => handlePressOption(4)}
            selected={rating === 4}
          />
        </Box>

        {visible && (
          <>
            <Box sx={styles.feedbackForm}>
              <Typography
                size={TypographySize.TextS}
                weight={TypographyWeight.Bold}
                color="var(--colors-light-text-text-white)"
                textStyle={{ textAlign: "left" }}
              >
                Please help us improve! What can be done better?
              </Typography>
              <Box sx={styles.chipContainer}>
                {feedbackTopics.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    onClick={() => handlePressChip(item)}
                    sx={[
                      styles.chip,
                      feedbackTopic === item && styles.chipSelected,
                    ]}
                    variant={feedbackTopic === item ? "outlined" : "filled"}
                  />
                ))}
              </Box>
              <TextField
                multiline
                rows={3}
                placeholder="Your comment"
                fullWidth
                variant="outlined"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                sx={styles.textField}
              />
            </Box>
            <Box sx={styles.checkboxContainer}>
              <Checkbox
                checked={contactConsent}
                onChange={(e) => setContactConsent(e.target.checked)}
              />
              <Typography
                size={TypographySize.TextS}
                weight={TypographyWeight.Regular}
                color="var(--colors-light-text-text-white)"
              >
                You can contact me about this feedback
              </Typography>
            </Box>
          </>
        )}
        <Box sx={styles.submitButtonContainer}>
          <MainButton
            text={isSubmitting ? "Submitting..." : "Submit Feedback"}
            disabled={isSubmitting || !rating}
            onClick={visible ? handleSubmit : onOpen}
            size={ButtonSize.md}
          />
        </Box>
      </Box>
    </Box>
  )
}

type FeedbackOptionProps = {
  Icon: React.ElementType
  text: string
  onClick: () => void
  selected: boolean
}

const FeedbackOption = ({
  Icon,
  text,
  onClick,
  selected,
}: FeedbackOptionProps) => {
  return (
    <Box
      sx={{
        borderRadius: "1rem",
        backgroundColor: selected ? selectedBg : unselectedBg,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        border: `2px solid ${selected ? getAccentColor() : "transparent"}`,
        "&:hover": {
          borderColor: getAccentColor(),
        },
        padding: { xs: "0.5rem", sm: "0.75rem" },
        transition: "border-color 0.2s ease",
      }}
      onClick={onClick}
    >
      <Icon
        size={"1rem"}
        style={{ width: "3rem", height: "3rem", color: "#6301fe" }}
      />
      <Typography
        size={TypographySize.TextS}
        weight={TypographyWeight.Bold}
        color="var(--primitives-desktop-primary-brand-violet-950)"
        textStyle={{
          marginTop: "0.25rem",
          textAlign: "center",
          fontSize: "0.75rem",
        }}
      >
        {text}
      </Typography>
    </Box>
  )
}

const styles = {
  modalOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "1rem",
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: "1rem",
    padding: "1.5rem",
    position: "relative",
    minWidth: { xs: "100%", sm: "600px" },
    maxWidth: { xs: "100%", sm: "600px" },
    backgroundColor: "var(--primitives-desktop-primary-white-20)",
    backdropFilter: "blur(10px)",
    width: "100%",
    overflow: "auto",
    maxHeight: "100%",
  },
  closeButton: {
    position: "absolute",
    top: "0.5rem",
    right: "0.5rem",
    color: "var(--colors-light-text-text-white)",
  },
  modalTitle: {
    fontWeight: "bold",
    textAlign: "center",
    width: "90%",
    color: "var(--colors-light-text-text-white)",
  },
  feedbackOptions: {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, 1fr)",
      sm: "repeat(4, 1fr)",
    },
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    margin: "1rem 0",
    gap: "0.5rem",
  },
  feedbackForm: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
  },
  feedbackPrompt: {
    fontWeight: "bold",
    marginBottom: "0.5rem",
    color: "var(--colors-light-text-text-white)",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  chip: {
    backgroundColor: unselectedBg,
    color: "var(--colors-light-text-text-title)",
    "&:hover": { backgroundColor: unselectedBg },
  },
  chipSelected: {
    backgroundColor: selectedBg,
    borderColor: "var(--colors-light-text-text-white)",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      color: "var(--colors-light-text-text-title)",
      backgroundColor: selectedBg,
      padding: "0.5rem",
      fontSize: "0.875rem",
      "& fieldset": {
        borderColor: "var(--primitives-desktop-primary-white-40)",
      },
      "&:hover fieldset": {
        borderColor: "var(--colors-light-text-text-white)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--colors-light-text-text-white)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "var(--colors-light-text-text-white)",
    },
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "1rem",
    "& .MuiCheckbox-root": {
      color: "var(--colors-light-text-text-white)",
    },
  },
  submitButtonContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
}
